import styled from 'styled-components';
import * as React from 'react';
import { Alert, SubmitButton } from '@ixicare/ui';
import { IndoorGeofencingMode, OutdoorGeofencingMode, ProfileConfiguration } from '@ixicare/resources';
import { FieldValue, FieldValues, isOptionObject } from '~modules/devices/components/DeviceConfigForm/schema';
import { serverErrors } from '~modules/devices/components/DeviceConfigForm/utils/serverErrors';
import { ControlOverrides } from '~modules/devices/components/DeviceConfigForm/fields/controls';
import { t } from '~/i18n';
import { PerimeterInput, ZonesInput } from '~modules/geofencing/inputs';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
import { formatSeconds, useResidentConfigForm } from '../hooks/useResidentConfigForm';
import { useProfileConfigSchema } from '../hooks/useProfileConfigSchema';
import SchemaSections from '~modules/devices/components/DeviceConfigForm/SchemaSections';
import StopGpsFixesModal from '../StopGpsFixesModal';
import InvertZonesModal from '../InvertZonesModal';

// A field id from the definition is a configuration key; the reducer is keyed by those and the
// renderer hands them back as plain strings.
type ConfigKey = keyof ProfileConfiguration;

interface Props {
  onSubmit: (data: ProfileConfiguration) => void;
  configuration?: ProfileConfiguration;
  profile: UserProfileEntry;
  isSubmitting: boolean;
  siteUuid?: string;
  saveError?: Error;
}

export default function ResidentConfigForm({
  onSubmit,
  configuration,
  profile,
  isSubmitting,
  siteUuid,
  saveError,
}: Props) {
  const { values, dispatch, site, indoor, outdoor, gps, problems, submit, handleSubmit } = useResidentConfigForm({
    configuration,
    profile,
    siteUuid,
    onSubmit,
  });
  // The definition the fields are drawn from. One section at a time, so each one can be compared
  // against what it replaced: the cadence field has no corrections over it, where geofencing has
  // four.
  const { schema } = useProfileConfigSchema(profile.id);
  // Every field is a carer's, and the backend gates the save -- the per-field role in the
  // definition is a rendering hint, and this page offers nothing an HCP may not set.
  // This is currently defined too loosely. Only a HCP with the correct permissions may see this.
  // This is fine for now, as this section is hidden conditionally by the parent component of the container.
  const gating = React.useMemo(() => ({ role: 'HEALTHCARE_PROVIDER' as const, permissions: [] as string[] }), []);

  // Bound locally so the guard below narrows it for the callbacks too: TypeScript drops the
  // narrowing of a property access inside a closure, where it keeps it for a binding.
  const { pendingPolarity } = indoor;

  // The definition names configuration keys; the sections hold them as their own state. Projected
  // into one bag for the renderer and routed back on change, so every correction stays where it
  // was rather than being reimplemented over a flat bag.
  const fieldValues = React.useMemo<FieldValues>(
    () => ({
      ...(values as FieldValues),
      'indoor-geofencing-enabled': indoor.enabled,
      'indoor-geofencing-mode': indoor.polarity,
      'indoor-geofencing-zones': indoor.zones,
      'indoor-geofencing-preset': indoor.presetId,
      // The corrected mode, not the one the carer picked -- which is what the checkbox and the
      // kind list both read, as they did when they were written by hand.
      'outdoor-geofencing-mode': outdoor.mode,
      'outdoor-geofencing-geo': outdoor.geo as FieldValue,
    }),
    [values, indoor.enabled, indoor.polarity, indoor.zones, indoor.presetId, outdoor.mode, outdoor.geo]
  );

  const handleFieldChange = (id: string, value: FieldValue) => {
    switch (id) {
      case 'indoor-geofencing-enabled':
        return indoor.setEnabled(value === true);
      // Through `handlePolarity`, which asks before inverting a selection rather than switching.
      case 'indoor-geofencing-mode':
        return indoor.handlePolarity(value as IndoorGeofencingMode);
      case 'indoor-geofencing-zones':
        return indoor.setZones((value ?? []) as string[]);
      case 'indoor-geofencing-preset':
        return indoor.setPresetId((value ?? null) as string | null);
      case 'outdoor-geofencing-mode':
        return outdoor.setMode(value as OutdoorGeofencingMode);
      case 'outdoor-geofencing-geo':
        return outdoor.setGeo(value as GeoJSON.Feature<GeoJSON.Polygon> | undefined);
      default:
        return dispatch({ field: id as ConfigKey, value });
    }
  };

  // The two controls no definition can describe, built from the site this page already knows.
  const controls = React.useMemo<ControlOverrides>(
    () => ({
      ZonePicker: ({ onChange: set }) =>
        siteUuid ? (
          <ZonesInput
            siteUuid={siteUuid}
            siteId={site.id}
            presets={site.presets}
            allZones={indoor.allZones}
            mode={indoor.polarity}
            selectedZones={indoor.zones}
            zonePresetId={indoor.presetId}
            onChange={(selection) => {
              set('indoor-geofencing-zones', selection.zoneIds);
              set('indoor-geofencing-preset', selection.zonePresetId);
            }}
          />
        ) : null,
      Polygon: ({ onChange: set }) => (
        <PerimeterInput
          lat={site.lat}
          long={site.long}
          defaultPolygon={site.defaultPerimeter}
          value={outdoor.hasPerimeter ? outdoor.geo : undefined}
          onChange={(geo) => set('outdoor-geofencing-geo', geo as FieldValue)}
        />
      ),
    }),
    [siteUuid, site, indoor.allZones, indoor.polarity, indoor.zones, indoor.presetId, outdoor.hasPerimeter, outdoor.geo]
  );

  /**
   * The definition, with any stored value it does not offer added as an option.
   *
   * A frequency set by hand, or before the nine presets settled, would otherwise not be selectable
   * -- and opening the page would silently move the resident to the nearest one. Keyed on the
   * loaded configuration rather than the live values, because it is a property of what was stored.
   *
   * No label: the definition labels the options it offers, and it has nothing to say about a value
   * it does not. The renderer falls back to the number itself, which reads as `45` beside
   * `45 sec` -- worth less than feeding a literal through `t()` and relying on i18next returning
   * an unknown key unchanged.
   */
  const offered = React.useMemo(() => {
    if (!schema) return undefined;
    const sections = Object.fromEntries(
      Object.entries(schema.sections).map(([id, section]) => [
        id,
        {
          ...section,
          fields: section.fields.map((field) => {
            const stored = (configuration as FieldValues | undefined)?.[field.id];
            if (!field.options?.length || stored === null || stored === undefined) return field;
            // The off state is stored like any other value but is not one of the kinds on offer:
            // the checkbox above the list owns it. Offering it as a choice would put "no outdoor
            // alarm" inside the list of outdoor alarms, reachable two ways that look different and
            // mean the same -- which is why the definition keeps it out of `options` and names it
            // separately.
            if (stored === field.off_value) return field;
            if (field.options.some((o) => (isOptionObject(o) ? o.value : o) === stored)) return field;
            return {
              ...field,
              options: [...field.options, { value: stored as string | number | boolean }],
            };
          }),
        },
      ])
    );
    return { ...schema, sections };
  }, [schema, configuration]);

  // What the last save was refused for, placed on the controls it named. `unplaced` is the case
  // where it named only fields this form has no control for -- the toast is suppressed whenever
  // the backend names any, so without it the save would fail in silence.
  const { byField: errors, unplaced } = React.useMemo(() => serverErrors(schema, saveError), [schema, saveError]);

  // Beside the outdoor alarm rather than with the problems above the submit button: this is not
  // something the carer got wrong, and it does not block the save - the save is the fix.
  const beside = gps.problem
    ? {
        'outdoor-geofencing-mode': (
          <Alert type="error">
            {t(gps.problem.key, {
              current: formatSeconds(gps.storedPeriod as number),
              derived: formatSeconds(gps.problem.derived),
            })}
          </Alert>
        ),
      }
    : undefined;

  return (
    <form onSubmit={handleSubmit}>
      {offered ? (
        <SchemaSections
          schema={offered}
          // The same payload, typed two ways: `ProfileConfiguration` names each key, the renderer's
          // bag is the looser view a generic form needs. A perimeter is the one value the two
          // disagree on -- a GeoJSON Feature is a JSON object at runtime but declares no index
          // signature. `Devices.ts` casts the schema the same way.
          values={fieldValues}
          // Nothing reads as changed: with no layer beneath it there is no "differs from what was
          // loaded" worth showing a carer, and `showFlags` turns the markers off regardless.
          initialValues={fieldValues}
          errors={errors}
          gating={gating}
          collapsed={{}}
          onToggleCollapsed={() => undefined}
          onChange={handleFieldChange}
          controls={controls}
          beside={beside}
          showFlags={false}
          showChrome={false}
          // On, and not the carer's to turn off while an allow list needs it. The kinds
          // inside it stay enabled: they still pick how the outdoor alarm works, only not
          // whether.
          disabledFields={indoor.requiresOutdoorCover ? ['outdoor-geofencing-mode'] : undefined}
        />
      ) : (
        // The definition could not be fetched, so there are no fields to draw. Everything on this
        // page comes from it now, and a carer is better told that than shown an empty panel.
        <Alert type="error">{t('device-config.error.schema')}</Alert>
      )}

      {(problems.length > 0 || unplaced) && (
        <Incomplete>
          {problems.map((key) => (
            <Alert key={key} type="error">
              {t(key)}
            </Alert>
          ))}
          {/* The backend named a field this form does not draw. The toast is suppressed whenever it
              names any, so without this the save would fail in silence. */}
          {unplaced && <Alert type="error">{t('profile-configuration.update.error')}</Alert>}
        </Incomplete>
      )}

      <SubmitButton loading={isSubmitting} disabled={problems.length > 0}>
        {t('global.update')}
      </SubmitButton>

      {gps.asking && (
        <StopGpsFixesModal
          currentInterval={formatSeconds(gps.storedPeriod as number)}
          onCancel={() => gps.setAsking(false)}
          onConfirm={(stopGpsFixes) => {
            gps.setAsking(false);
            submit(stopGpsFixes);
          }}
        />
      )}

      {pendingPolarity && (
        <InvertZonesModal
          to={pendingPolarity}
          selectedCount={indoor.zones.length}
          invertedCount={indoor.inverted.length}
          // The zone list fills in asynchronously; inverting against an empty one would wipe the
          // selection outright.
          canInvert={indoor.allZones.length > 0}
          onInvert={() => {
            indoor.setZones(indoor.inverted);
            indoor.detachOnBlock(pendingPolarity);
            indoor.setPolarity(pendingPolarity);
            indoor.setPendingPolarity(undefined);
          }}
          onKeep={() => {
            indoor.detachOnBlock(pendingPolarity);
            indoor.setPolarity(pendingPolarity);
            indoor.setPendingPolarity(undefined);
          }}
          // Leaves the polarity untouched. The re-render this causes is what puts the tick back on
          // the radio the carer clicked away from.
          onCancel={() => indoor.setPendingPolarity(undefined)}
        />
      )}
    </form>
  );
}

const Incomplete = styled.div`
  margin-bottom: 14px;

  > * + * {
    margin-top: 8px;
  }
`;
